import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { TestimonialsProps } from 'lib/components/Testimonials/Testimonials'

import { IconListItem } from '@/components/IconList'

export interface PartnershipBenefitsPageContent {
  hero: NarrativeHeroProps
  fifties: FiftyFiftyProps[]
  partnershipTestimonials: TestimonialsProps
  whatInItForYouHeader: string
  whatInItForYouList: IconListItem[]
  whatDoesItMeanHeader: string
  whatDoesItMeanList: IconListItem[]
}

const BACKGROUND_IMAGE = '/images/shipskis/become-a-partner-hero.jpg'

const hero: NarrativeHeroProps = {
  backgroundImage: BACKGROUND_IMAGE,
  header: 'Become a partner',
  subHeader: 'A better experience for you, your guests, and your team.',
}

const fifties: FiftyFiftyProps[] = [
  {
    image: {
      src: '/images/shipskis/become-a-partner-1.jpg',
    },
    backgroundColor: 'white',
    header: 'Lighten their load',
    layout: 'image-right',
    button: {
      text: 'Become a partner',
      link: {
        href: '#lead-form',
      },
    },
    body: `
            By joining forces with ShipSkis, you're giving your guests a
            better experience right from the start - and giving your team a
            simpler way to support them.`,
    showEyebrow: false,
  },
  {
    image: {
      src: '/images/shipskis/become-a-partner-2.jpg',
    },
    backgroundColor: 'white',
    header: "You're in good company",
    layout: 'image-left',
    body: `
            We partner with more than 3,500 destinations around the world,
            from Deer Valley and Telluride to Aspen Snowmass and Squaw Valley.
            They trust us to deliver exceptional service. You can,
            too.
          `,
    showEyebrow: false,
  },
]

const partnershipTestimonials: TestimonialsProps = {
  title: (
    <>
      What our partners <strong>have&nbsp;to&nbsp;say</strong>
    </>
  ),
  testimonials: [
    {
      stars: 5,
      quote: `
        Partnering with ShipSkis has made a huge difference for both our guests and our staff at
        Black Diamond Lodge. With the gear and luggage delivered directly to the property, our team
        is able to focus more on providing exceptional service instead of managing logistics.
        It's really streamlined everything, and that means a smoother experience for everyone
        involved, guests and staff alike.
      `,
      attribution: {
        icon: 'resort',
        name: 'Tara H',
        location: 'Black Diamond Lodge, Deer Valley Resort',
      },
    },
    {
      stars: 5,
      quote: `
        ShipSkis has become indispensable in helping us create unforgettable experiences for our
        guests while simplifying shipping for both our hotel team and their convenience. The QR
        codes and personalized Viceroy Snowmass website have made sending and receiving items a
        breeze, streamlining operations and enhancing the guest experience. We highly recommend
        their services to any property looking to boost efficiency and customer satisfaction—it's
        been a game-changer for us!
      `,
      attribution: {
        icon: 'resort',
        name: 'Aubrey P',
        location: 'Viceroy Snowmass',
      },
    },
  ],
}

const whatInItForYouHeader: string = "What's in it for you?"

const whatInItForYouList: IconListItem[] = [
  {
    icon: 'check-circle-filled',
    header: 'Added value without added work',
    body: (
      <>
        We handle the shipping logistics. You deliver the experience. Our team supports your staff
        every step of the way.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Onsite signage & digital resources',
    body: (
      <>
        From custom landing pages to co-branded materials, we make integration easy and effortless
        for your team.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Revenue opportunities',
    body: (
      <>
        Earn commission on every shipment placed through your channel, along with discounted
        shipping
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Increased guest satisfaction',
    body: (
      <>
        Set the tone for a standout stay by offering a premium, hassle-free shipping option that
        your guests will thank you for.
      </>
    ),
  },
]

const whatDoesItMeanHeader: string = 'What does that mean for your guests?'

const whatDoesItMeanList: IconListItem[] = [
  {
    icon: 'check-circle-filled',
    header: 'Stress-free travel',
    body: (
      <>
        No more lugging ski bags through airports or squeezing them into rental cars. Guests enjoy
        seamless door-to-door shipping, arriving fresh and ready to shred.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Trusted by travelers worldwide',
    body: (
      <>
        With more than 500,000 bags shipped annually, ShipSkis is the most trusted name in winter
        travel.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Premium experience',
    body: (
      <>
        Real-time tracking, insurance options, and top-tier customer support mean your guests
        receive 5-star treatment from start to finish.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Travel light, arrive happy',
    body: (
      <>
        When their skis are already waiting at the mountain, the vacation starts sooner and lasts
        longer.
      </>
    ),
  },
]

const content: PartnershipBenefitsPageContent = {
  hero,
  fifties,
  partnershipTestimonials,
  whatInItForYouHeader,
  whatInItForYouList,
  whatDoesItMeanHeader,
  whatDoesItMeanList,
}

export default content
